import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BookOpen, Search, Bookmark, BookmarkCheck, Coffee, Sun, Moon,
  Cookie, Flame, Beef, Zap, Droplet, ChefHat, ChevronDown,
  ChevronUp, UtensilsCrossed, Filter, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { SEOMeta } from '@/components/SEOMeta';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MEAL_CONFIG = {
  breakfast: { icon: Coffee,  label: 'Закуска',  accent: 'text-amber-600',   bg: 'bg-amber-100 dark:bg-amber-900/30',   dot: 'bg-amber-400'   },
  lunch:     { icon: Sun,     label: 'Обяд',     accent: 'text-sky-600',     bg: 'bg-sky-100 dark:bg-sky-900/30',       dot: 'bg-sky-400'     },
  dinner:    { icon: Moon,    label: 'Вечеря',   accent: 'text-violet-600',  bg: 'bg-violet-100 dark:bg-violet-900/30', dot: 'bg-violet-400'  },
  snack:     { icon: Cookie,  label: 'Снак',     accent: 'text-emerald-600', bg: 'bg-emerald-100 dark:bg-emerald-900/30', dot: 'bg-emerald-400' },
};

const TYPE_FILTERS = [
  { key: 'all',       label: 'Всички'  },
  { key: 'breakfast', label: 'Закуска' },
  { key: 'lunch',     label: 'Обяд'    },
  { key: 'dinner',    label: 'Вечеря'  },
  { key: 'snack',     label: 'Снак'    },
  { key: 'saved',     label: 'Запазени' },
];

/* ── Recipe Card ── */
const RecipeCard = ({ meal, onToggleSave, idx }) => {
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const cfg = MEAL_CONFIG[meal.type] || MEAL_CONFIG.lunch;
  const Icon = cfg.icon;

  const handleToggle = async (e) => {
    e.stopPropagation();
    setSaving(true);
    await onToggleSave(meal);
    setSaving(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: idx * 0.04 }}
    >
      <Card
        className="group rounded-2xl overflow-hidden hover:shadow-lg transition-shadow duration-300"
        style={{ boxShadow: '0 1px 0 rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.05)', border: 'none' }}
      >
        {/* Card header — type badge + bookmark */}
        <div className={`${cfg.bg} px-4 py-3 flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-white/70 dark:bg-black/20 shadow-sm flex items-center justify-center">
              <Icon className={`w-3.5 h-3.5 ${cfg.accent}`} />
            </div>
            <span className={`text-[11px] font-bold uppercase tracking-widest ${cfg.accent}`}>
              {meal.type_label || cfg.label}
            </span>
          </div>
          <button
            onClick={handleToggle}
            disabled={saving}
            data-testid={`bookmark-btn-${meal.name?.slice(0,10).replace(/\s/g,'-')}`}
            className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-200 ${
              meal.is_saved
                ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                : 'bg-white/70 dark:bg-slate-700/70 text-gray-400 dark:text-gray-500 hover:bg-white dark:hover:bg-slate-700 hover:text-emerald-600 dark:hover:text-emerald-400'
            } ${saving ? 'opacity-50' : ''}`}
            title={meal.is_saved ? 'Премахни от запазени' : 'Запази рецептата'}
          >
            {meal.is_saved
              ? <BookmarkCheck className="w-4 h-4" />
              : <Bookmark className="w-4 h-4" />
            }
          </button>
        </div>

        {/* Card body */}
        <CardContent className="p-4">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm leading-snug mb-3 min-h-[2.5rem]">
            {meal.name}
          </h3>

          {/* Calories + macros */}
          <div className="flex items-center gap-1.5 mb-3">
            <div className="flex items-center gap-1 bg-orange-50 dark:bg-orange-900/20 rounded-lg px-2 py-1">
              <Flame className="w-3 h-3 text-orange-400" />
              <span className="text-xs font-bold text-gray-800 dark:text-gray-200 tabular-nums">{meal.calories}</span>
              <span className="text-[10px] text-gray-400 dark:text-gray-500">ккал</span>
            </div>
            <div className="flex items-center gap-1 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg px-2 py-1">
              <Beef className="w-3 h-3 text-emerald-500" />
              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 tabular-nums">{meal.proteins}<span className="text-[9px] text-gray-400">г</span></span>
            </div>
            <div className="flex items-center gap-1 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-2 py-1">
              <Zap className="w-3 h-3 text-amber-500" />
              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 tabular-nums">{meal.carbs}<span className="text-[9px] text-gray-400">г</span></span>
            </div>
            <div className="flex items-center gap-1 bg-sky-50 dark:bg-sky-900/20 rounded-lg px-2 py-1">
              <Droplet className="w-3 h-3 text-sky-500" />
              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 tabular-nums">{meal.fats}<span className="text-[9px] text-gray-400">г</span></span>
            </div>
          </div>

          {/* Ingredients preview */}
          {meal.ingredients?.length > 0 && (
            <div className="mb-3">
              <p className="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1.5">Съставки</p>
              <div className="flex flex-wrap gap-1">
                {meal.ingredients.slice(0, 4).map((ing, i) => (
                  <span key={i} className="inline-block text-[10px] text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-slate-700 rounded-full px-2 py-0.5">
                    {ing.length > 22 ? ing.slice(0, 22) + '…' : ing}
                  </span>
                ))}
                {meal.ingredients.length > 4 && (
                  <span className="inline-block text-[10px] text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-slate-700 rounded-full px-2 py-0.5">
                    +{meal.ingredients.length - 4} още
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Recipe toggle */}
          {meal.recipe?.length > 0 && (
            <>
              <div className="h-px bg-gray-100 dark:bg-slate-700 mb-2.5" />
              <button
                onClick={() => setExpanded(v => !v)}
                className="w-full flex items-center justify-between py-0.5 group"
                data-testid={`expand-recipe-${meal.name?.slice(0,10).replace(/\s/g,'-')}`}
              >
                <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors">
                  <ChefHat className="w-3.5 h-3.5 text-emerald-500" />
                  Начин на приготвяне
                </span>
                {expanded
                  ? <ChevronUp className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                  : <ChevronDown className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                }
              </button>
              <AnimatePresence initial={false}>
                {expanded && (
                  <motion.div
                    key="recipe"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <ol className="mt-3 space-y-2">
                      {meal.recipe.map((step, i) => (
                        <li key={i} className="flex gap-2.5">
                          <span className="flex-shrink-0 w-4 h-4 rounded-full bg-gray-900 dark:bg-slate-600 text-white font-bold flex items-center justify-center text-[9px] mt-0.5">
                            {i + 1}
                          </span>
                          <span className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

/* ── Skeleton Card ── */
const SkeletonCard = () => (
  <div className="rounded-2xl border border-gray-100 dark:border-slate-700 overflow-hidden animate-pulse">
    <div className="h-12 bg-gray-100 dark:bg-slate-700" />
    <div className="p-4 space-y-3">
      <div className="h-4 bg-gray-100 dark:bg-slate-700 rounded w-3/4" />
      <div className="h-4 bg-gray-100 dark:bg-slate-700 rounded w-1/2" />
      <div className="flex gap-1.5">
        {[1,2,3,4].map(i => <div key={i} className="h-6 bg-gray-100 dark:bg-slate-700 rounded-lg w-14" />)}
      </div>
    </div>
  </div>
);

/* ── Main Page ── */
export default function CookbookPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  const fetchCookbook = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cookbook`);
      setMeals(res.data.meals || []);
    } catch {
      toast.error('Грешка при зареждане на готварската книга');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCookbook(); }, [fetchCookbook]);

  const handleToggleSave = async (meal) => {
    if (meal.is_saved && meal.saved_recipe_id) {
      try {
        await axios.delete(`${API}/cookbook/save/${meal.saved_recipe_id}`);
        setMeals(prev => prev.map(m =>
          m.name === meal.name ? { ...m, is_saved: false, saved_recipe_id: null } : m
        ));
        toast.success('Премахнато от запазените');
      } catch {
        toast.error('Грешка при премахване');
      }
    } else {
      try {
        const res = await axios.post(`${API}/cookbook/save`, {
          meal_name: meal.name,
          meal_data: {
            type: meal.type, type_label: meal.type_label,
            calories: meal.calories, proteins: meal.proteins,
            carbs: meal.carbs, fats: meal.fats,
            ingredients: meal.ingredients, recipe: meal.recipe,
          },
          plan_id: meal.plan_id || '',
        });
        setMeals(prev => prev.map(m =>
          m.name === meal.name ? { ...m, is_saved: true, saved_recipe_id: res.data.id } : m
        ));
        toast.success('Рецептата е запазена!');
      } catch (e) {
        if (e?.response?.status === 409) toast.info('Вече е запазена');
        else toast.error('Грешка при запазване');
      }
    }
  };

  // Filter + search
  const filtered = meals.filter(m => {
    if (activeFilter === 'saved' && !m.is_saved) return false;
    if (activeFilter !== 'all' && activeFilter !== 'saved' && m.type !== activeFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return m.name.toLowerCase().includes(q) ||
        m.ingredients?.some(ing => ing.toLowerCase().includes(q));
    }
    return true;
  });

  const savedCount = meals.filter(m => m.is_saved).length;

  return (
    <>
      <SEOMeta
        title="Готварска книга"
        description="Всички рецепти, генерирани специално за теб — запазени на едно място."
        canonical="/cookbook"
        noindex={true}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>

          {/* ── Header ── */}
          <div className="mb-8 flex flex-col sm:flex-row sm:items-end gap-4 justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center shadow-md">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
                  Готварска книга
                </h1>
              </div>
              <p className="text-slate-500 dark:text-slate-400 text-sm ml-[52px]">
                Всички рецепти, генерирани специално за теб
                {meals.length > 0 && (
                  <> · <span className="font-semibold text-gray-700 dark:text-gray-300">{meals.length} рецепти</span></>
                )}
                {savedCount > 0 && (
                  <> · <span className="text-emerald-600 dark:text-emerald-400 font-semibold">{savedCount} запазени</span></>
                )}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/my-plan')}
              className="flex items-center gap-2 text-sm border-gray-200 dark:border-slate-600 self-start sm:self-auto"
            >
              <UtensilsCrossed className="w-3.5 h-3.5 text-emerald-600" />
              Генерирай нов план
            </Button>
          </div>

          {/* ── Search + Filter ── */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            {/* Search */}
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <input
                data-testid="cookbook-search"
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Търси по ястие или съставка…"
                className="w-full pl-9 pr-8 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 dark:focus:ring-emerald-900/30 transition-all"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Filter chips */}
            <div className="flex items-center gap-1.5 flex-wrap">
              <Filter className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
              {TYPE_FILTERS.map(f => (
                <button
                  key={f.key}
                  onClick={() => setActiveFilter(f.key)}
                  data-testid={`filter-${f.key}`}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-150 ${
                    activeFilter === f.key
                      ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 shadow-sm'
                      : 'bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-slate-500 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                >
                  {f.key === 'saved' && <BookmarkCheck className="w-3 h-3" />}
                  {f.label}
                  {f.key === 'saved' && savedCount > 0 && (
                    <span className={`ml-0.5 rounded-full px-1 text-[9px] font-bold ${activeFilter === 'saved' ? 'bg-white/20 text-white' : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'}`}>
                      {savedCount}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* ── Content ── */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array(6).fill(0).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : meals.length === 0 ? (
            /* Empty state — no plans yet */
            <div className="text-center py-20">
              <div className="w-20 h-20 rounded-3xl bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 flex items-center justify-center mx-auto mb-5">
                <BookOpen className="w-9 h-9 text-gray-300 dark:text-slate-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">
                Готварската книга е празна
              </h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-6 max-w-xs mx-auto">
                Генерирай своя първи хранителен план и всички рецепти ще се появят тук автоматично.
              </p>
              <Button
                onClick={() => navigate('/my-plan')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="goto-plan-btn"
              >
                <UtensilsCrossed className="w-4 h-4 mr-2" />
                Генерирай план
              </Button>
            </div>
          ) : filtered.length === 0 ? (
            /* No results */
            <div className="text-center py-16">
              <p className="text-gray-400 dark:text-gray-500 text-sm">
                Няма рецепти, отговарящи на търсенето.
              </p>
              <button onClick={() => { setSearch(''); setActiveFilter('all'); }}
                className="mt-3 text-emerald-600 dark:text-emerald-400 text-sm font-medium hover:underline">
                Изчисти филтрите
              </button>
            </div>
          ) : (
            <div
              data-testid="cookbook-grid"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              {filtered.map((meal, idx) => (
                <RecipeCard
                  key={`${meal.name}-${idx}`}
                  meal={meal}
                  onToggleSave={handleToggleSave}
                  idx={idx}
                />
              ))}
            </div>
          )}

          {/* Results count */}
          {!loading && filtered.length > 0 && (
            <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-8">
              Показани {filtered.length} от {meals.length} рецепти
            </p>
          )}
        </motion.div>
      </div>
    </>
  );
}
